import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl } from '../api/client';
import { useAuth } from '../context/AuthContext';

const AuthCallback: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const { refreshUser } = useAuth();

  useEffect(() => {
    const abortController = new AbortController();
    let isActive = true; // Track if this effect instance is still active

    const fetchUser = async () => {
      try {
        // Check for token in URL (cross-domain OAuth flow)
        const urlToken = searchParams.get('token');

        const headers: HeadersInit = {};
        if (urlToken) {
          headers['Authorization'] = `Bearer ${urlToken}`;
        }

        const response = await fetch(`${getApiBaseUrl()}/api/users/me`, {
          credentials: 'include', // Also try cookies
          headers,
          signal: abortController.signal,
        });

        // Check if this effect instance is still active before updating state
        if (!isActive) return;

        if (response.ok) {
          const userData = await response.json();
          localStorage.setItem('userInfo', JSON.stringify(userData));

          // Update AuthContext state so protected routes see the user
          await refreshUser();

          // Check if this effect instance is still active after async call
          if (!isActive) return;

          // Clean URL by removing token parameter
          window.history.replaceState({}, '', '/auth/callback');

          // Redirect based on user role
          if (userData.isSuperAdmin) {
            navigate('/super-admin', { replace: true });
          } else if (userData.currentMembership?.role?.type === 'admin') {
            navigate('/admin/calendar', { replace: true });
          } else {
            navigate('/dashboard', { replace: true });
          }
        } else {
          setError(t('auth.loginFailed'));
          setTimeout(() => {
            if (isActive) {
              navigate('/login', { replace: true });
            }
          }, 2000);
        }
      } catch (err: any) {
        // Ignore abort errors (happens on cleanup)
        if (err.name === 'AbortError') return;

        // Check if this effect instance is still active before updating state
        if (!isActive) return;

        console.error('Auth callback error:', err);
        setError(t('auth.loginFailed'));
        setTimeout(() => {
          if (isActive) {
            navigate('/login', { replace: true });
          }
        }, 2000);
      }
    };

    fetchUser();

    // Cleanup: abort fetch and mark this effect instance as inactive
    return () => {
      isActive = false;
      abortController.abort();
    };
  }, [navigate, t, searchParams, refreshUser]);

  if (error) {
    return (
      <div className="page-content">
        <div className="form-container">
          <p style={{ color: 'red' }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="form-container">
        <p>{t('auth.authenticating') || 'Authenticating...'}</p>
      </div>
    </div>
  );
};

export default AuthCallback;
